import type { BffApiClient } from "@/app/api-client"
import { getRawAddress } from "@anaconda/core/blockchain/address"
import type { NftCollectionType } from "@anaconda/core/nft-collection/collection/types"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import {
  type SolanaCollectionNftStandard,
  parseSolanaCollectionNftStandard,
} from "@rarible_int/anaconda_sdk/solana"

export type { SolanaCollectionNftStandard }
export { parseSolanaCollectionNftStandard }

export type ResolvedSolanaCollectionOfferStandard =
  | {
      standard: SolanaCollectionNftStandard
      collectionMint: string
    }
  | {
      standard: "UNSUPPORTED"
      collectionMint: null
    }

/**
 * Prefer `collection.type` / `nft.collection.type` when it already names a
 * known Solana standard (including `TOKEN_GROUP_2022` → `TOKEN2022`).
 */
export function resolveSolanaCollectionOfferStandardFromType(params: {
  collectionId: CollectionId | string
  collectionType: NftCollectionType | string | undefined
}): ResolvedSolanaCollectionOfferStandard | undefined {
  const standard = parseSolanaCollectionNftStandard(params.collectionType)
  if (!standard) return undefined
  // TOKEN_GROUP_2022 / TOKEN2022 from the marketplace API is unreliable
  // (Metaplex collections have been mislabeled). Force BFF on-chain check.
  if (standard === "TOKEN2022") return undefined

  return {
    standard,
    collectionMint: getRawAddress(params.collectionId),
  }
}

/** BFF on-chain/DAS fallback when `collection.type` is coarse (e.g. `SOLANA`). */
export async function fetchSolanaCollectionOfferStandard(
  bffApiClient: BffApiClient,
  collectionId: CollectionId | string,
): Promise<ResolvedSolanaCollectionOfferStandard> {
  const response = await bffApiClient.api.collections[":id"][
    "solana-nft-standard"
  ].$get({ param: { id: collectionId } })

  if (!response.ok) throw await response.json()

  const body = await response.json()
  if (body.standard === "UNSUPPORTED" || !body.collectionMint) {
    return { standard: "UNSUPPORTED", collectionMint: null }
  }

  const standard = parseSolanaCollectionNftStandard(body.standard)
  if (!standard) {
    return { standard: "UNSUPPORTED", collectionMint: null }
  }

  return { standard, collectionMint: body.collectionMint }
}

export function formatSolanaCollectionStandardLabel(
  type: NftCollectionType | string | undefined,
): string {
  if (!type) return "-"
  const standard = parseSolanaCollectionNftStandard(type)
  return (standard ?? type).replaceAll("_", "-")
}

/** WNS / Token-2022 collections have no useful on-chain collection contract to show. */
export function shouldHideSolanaCollectionContractAddress(
  type: NftCollectionType | string | undefined,
): boolean {
  const standard = parseSolanaCollectionNftStandard(type)
  return standard === "WNS" || standard === "TOKEN2022"
}
